import { ServiceButtonExternal } from "@/components/molecules/ServiceButton";
import { externalServiceByCode } from "@/libs/defs";
import { useAlerts } from "@/libs/hooks/useAlerts";
import { useBillingBankServices } from "@/libs/hooks/useBillingBankServices";
import { useCompany } from "@/libs/hooks/useCompany";
import {
  Button,
  Font,
  Logo,
  TextLink,
} from "@moneyforward/mfx_biz_react_components";
import { MouseEventHandler, useMemo } from "react";
import { useIsSpMode } from "@/hooks";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import {
  AlertCode,
  AlertLinkType,
  CompanyStatus,
  Service,
  ServiceStatus,
  ServiceType,
} from "@/generated/graphql_billing";
import { useRouter } from "next/router";
import { Alert } from "../atoms";
import config from "@/libs/config";
import { BankCode, Env, FAQ_URL } from "@/libs/constants";
import { isForcedCancellation } from "@/libs/billing/company";
import { calcAvailableServices } from "@/libs/billing/service";
import RelatedServiceLoanPortalIconIcon from "@/assets/RelatedServiceLoanPortalIcon.svg";

type Props = {
  isAdminUsersAtMikatanoID: boolean;
  companyId: string;
  displayCompanyId: string;
  stage: string;
  clickDXFButton: MouseEventHandler;
  clickInvoiceButton: MouseEventHandler;
  clickBFMButton: MouseEventHandler;
  clickLoanButton: MouseEventHandler;
  clickManageUsersButton: MouseEventHandler;
  clickInvoiceLocalhostButton: MouseEventHandler;
  clickBFMLocalhostButton: MouseEventHandler;
  clickBFMV2LocalhostButton: MouseEventHandler;
  clickBFMV2StagingButton: MouseEventHandler;
  clickCFMLocalhostButton: MouseEventHandler;
  clickCFMStagingButton: MouseEventHandler;
};

/**
 * 関連サービスの設定情報を保持する型
 * en: Type that holds the configuration information of related services
 * @type {Object} RelatedServiceConfig
 * @property {string} serviceName - サービス名, en: Service name
 * @property {JSX.Element} icon - サービスのアイコン, en: Service icon
 * @property {MouseEventHandler} onClick - クリック時のイベント, en: Click event
 */
type RelatedServiceConfig = {
  serviceName: string;
  icon: JSX.Element;
  onClick: MouseEventHandler;
};

export const SelectServiceButtons = (props: Props) => {
  const router = useRouter();
  const isSpMode = useIsSpMode();
  const bankcode = config.BANK_CODE;

  const { company, companyLoading } = useCompany();
  const { bankServices, billingBankServicesLoading } = useBillingBankServices();
  const { alerts, alertsLoading } = useAlerts();

  const { mikatanoServices, externalServices } = calcAvailableServices(
    company?.availableServices
  );

  const unContractedServices = bankServices
    ? bankServices.services
        .filter((bankService) => bankService.code !== "LOAN")
        .filter((bankService) => bankService.type === ServiceType.Mikatano)
        .filter(
          (bankService) =>
            !mikatanoServices.find((service) => service?.id === bankService?.id)
        )
    : [];

  const serviceConfigs: {
    serviceName: string;
    notContractedLogo: JSX.Element;
    contractedLogo: JSX.Element;
    notContractedText: JSX.Element;
    contractedText: JSX.Element;
    onClick: MouseEventHandler;
  }[] = [
    {
      serviceName: "dxf",
      notContractedLogo: <Logo variant="dxf" color={config.BANK_COLOR} />,
      contractedLogo: <Logo variant="dxfVertical" color={config.BANK_COLOR} />,
      notContractedText: (
        <>
          <Font>
            業務のペーパーレス化や、情報把握にかかる時間削減に繋がるDX支援のサービスです。
            <br />
            全従業員がより一層本業に集中できる環境を整えます。
          </Font>
          <div css={textBox}>
            <TextLink
              href={config.LP_URL_DXF}
              target="_blank"
              rel="noopener noreferrer"
            >
              Mikatano ワークスの詳細はこちら
            </TextLink>
          </div>
        </>
      ),
      contractedText: (
        <Font>業務のペーパーレス化などDX支援のサービスです。</Font>
      ),
      onClick: props.clickDXFButton,
    },
    {
      serviceName: "bfm",
      notContractedLogo: <Logo variant="bfm" color={config.BANK_COLOR} />,
      contractedLogo: <Logo variant="bfmVertical" color={config.BANK_COLOR} />,
      notContractedText: (
        <>
          <Font>
            複数の金融機関の残高を無料でまとめて管理します。複数口座の入出金の明細がデータで一覧化され、検索も簡単に行えます。利用状況を確認する手間が大幅に削減されます。
            <br />
          </Font>
          <div css={textBox}>
            <TextLink
              href={config.LP_URL_BFM}
              target="_blank"
              rel="noopener noreferrer"
            >
              Mikatano 資金管理の詳細はこちら
            </TextLink>
          </div>
        </>
      ),
      contractedText: (
        <Font>複数の金融機関の残高や入出金の明細をまとめて管理します。</Font>
      ),
      onClick: props.clickBFMButton,
    },
    {
      serviceName: "invoice",
      notContractedLogo: <Logo variant="invoice" color={config.BANK_COLOR} />,
      contractedLogo: (
        <Logo variant="invoiceVertical" color={config.BANK_COLOR} />
      ),
      notContractedText: (
        <>
          <Font>
            受領・発行した請求書をシンプルに一元管理し、毎月の入出金予定を見える化することで経理業務の効率化を支援するサービスです。
          </Font>
          <div css={textBox}>
            <TextLink
              href={config.LP_URL_INVOICE}
              target="_blank"
              rel="noopener noreferrer"
            >
              Mikatano インボイス管理の詳細はこちら
            </TextLink>
          </div>
        </>
      ),
      contractedText: (
        <Font>
          発行・受取請求書両方の管理によって経理業務の手間を省きます。
        </Font>
      ),
      onClick: props.clickInvoiceButton,
    },
  ];

  const relatedServiceConfigs: {
    serviceName: string;
    icon: JSX.Element;
    onClick: MouseEventHandler;
  }[] = [
    {
      serviceName: "LOAN",
      icon: (
        <img
          src={RelatedServiceLoanPortalIconIcon.src}
          alt="Service Icon of Loan Portal"
        />
      ),
      onClick: props.clickLoanButton,
    },
  ];

  const relatedServices = bankServices
    ? bankServices.services.filter(
        (bankService) =>
          relatedServiceConfigs.find(
            (config) => config.serviceName === bankService.code
          ) && bankService.status === ServiceStatus.Active
      )
    : [];

  const relatedServiceBoxes = (
    relatedServices: Service[],
    relatedServiceConfigs: RelatedServiceConfig[],
    isSpMode: boolean
  ) => {
    return relatedServices.map((relatedService) => (
      <div
        key={relatedService.id}
        css={relatedServiceThinBoxWithIcon(isSpMode)}
        onClick={
          relatedServiceConfigs.find(
            (config) => config.serviceName === relatedService.code
          )?.onClick
        }
      >
        <div css={iconInBoxWithIcon}>
          {
            relatedServiceConfigs.find(
              (config) => config.serviceName === relatedService.code
            )?.icon
          }
        </div>
        <div
          key={`relatedService-${relatedService.id}`}
          css={relatedServiceThinBoxWithIconText(isSpMode)}
        >
          <Font variant="mb">{relatedService.name}</Font>
        </div>
      </div>
    ));
  };

  const externalServicesInContractedPlanFlexbox = (
    externalServicesInContractedPlan: Service[]
  ) => {
    const services = externalServicesInContractedPlan
      .filter((svc) => externalServiceByCode(svc.code))
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((svc: Service) => externalServiceByCode(svc.code));

    return services.map((svc) => {
      return (
        <ServiceButtonExternal
          key={svc.code}
          service={svc}
          suspended={suspendedAlerts.length > 0}
          displayCompanyID={props.displayCompanyId}
        />
      );
    });
  };

  const totalRelatedServicesLength =
    relatedServices.length + externalServices.length;

  const reversalNgAlerts = alerts.filter(
    (alert) => alert.code === AlertCode.ReversalNg
  );
  const firstBillingNgAlerts = alerts.filter(
    (alert) => alert.code === AlertCode.FirstBillingNg
  );
  const secondBillingNgAlerts = alerts.filter(
    (alert) => alert.code === AlertCode.SecondBillingNg
  );
  const suspendedAlerts = alerts.filter(
    (alert) => alert.code === AlertCode.Suspended
  );

  const ready =
    !companyLoading && !billingBankServicesLoading && !alertsLoading;

  if (!ready) {
    return;
  }

  return (
    <div css={container(isSpMode)}>
      {alerts.length > 0 && (
        <div css={alertBox}>
          {reversalNgAlerts.length > 0 && (
            <Alert
              alerts={[
                {
                  alertID: reversalNgAlerts[0].alertID,
                  alertType: reversalNgAlerts[0].alertType,
                  linkType: AlertLinkType.Internal,
                  linkURL: "/contract-management",
                  message: reversalNgAlerts[0].message,
                },
              ]}
              linkText="契約管理画面へ"
            />
          )}
          {firstBillingNgAlerts.length > 0 && (
            <Alert
              alerts={[
                {
                  alertID: firstBillingNgAlerts[0].alertID,
                  alertType: firstBillingNgAlerts[0].alertType,
                  linkType: AlertLinkType.Internal,
                  linkURL: "/contract-management",
                  message: firstBillingNgAlerts[0].message,
                },
              ]}
              linkText="契約管理画面へ"
            />
          )}
          {secondBillingNgAlerts.length > 0 && (
            <Alert
              alerts={[
                {
                  alertID: secondBillingNgAlerts[0].alertID,
                  alertType: secondBillingNgAlerts[0].alertType,
                  linkType: AlertLinkType.External,
                  linkURL: FAQ_URL,
                  message: secondBillingNgAlerts[0].message,
                },
              ]}
              linkText="詳しくはこちら"
            />
          )}
          {suspendedAlerts.length > 0 && (
            <Alert
              alerts={[
                {
                  alertID: suspendedAlerts[0].alertID,
                  alertType: suspendedAlerts[0].alertType,
                  linkType: AlertLinkType.External,
                  linkURL: FAQ_URL,
                  message: suspendedAlerts[0].message,
                },
              ]}
              linkText="詳しくはこちら"
            />
          )}
        </div>
      )}
      {mikatanoServices.length === 0 ? (
        <div>
          <Font variant="lb">Mikatano シリーズ サービス一覧</Font>
          <div css={isNotContractedBox}>
            <div css={boxContainer(isSpMode)}>
              {unContractedServices.map((service, index) => (
                <div key={service.id} css={flexBoxContainer(isSpMode)}>
                  {index > 0 && <div css={dividedLine(isSpMode)}></div>}
                  <div css={box}>
                    {
                      serviceConfigs.find(
                        (config) =>
                          config.serviceName === service.code.toLowerCase()
                      )?.notContractedLogo
                    }
                    <div css={textBox}>
                      {
                        serviceConfigs.find(
                          (config) =>
                            config.serviceName === service.code.toLowerCase()
                        )?.notContractedText
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div css={subBox}>
            {props.isAdminUsersAtMikatanoID ? (
              <div>
                <Button
                  onClick={() => router.push("/plan/select-plan")}
                  disabled={isForcedCancellation(company)}
                >
                  プラン選択
                </Button>
              </div>
            ) : (
              <Font>
                サービスの利用登録がされていません。利用登録が完了するまでしばらくお待ちください。
              </Font>
            )}
          </div>
        </div>
      ) : (
        <div>
          <Font variant="lb">利用中のサービス</Font>
          <div css={thinBoxContainer(isSpMode)}>
            {mikatanoServices
              .filter((service) => service.status === ServiceStatus.Active)
              .sort(
                (serviceBefore, serviceAfter) =>
                  (serviceBefore.displayOrder || 0) -
                  (serviceAfter.displayOrder || 0)
              )
              .map((service) => (
                <div key={service.id} css={contractedFlexBox(isSpMode)}>
                  <div
                    css={thinBox(isSpMode)}
                    onClick={
                      serviceConfigs.find(
                        (config) =>
                          config.serviceName === service.code.toLowerCase()
                      )?.onClick
                    }
                  >
                    {
                      serviceConfigs.find(
                        (config) =>
                          config.serviceName === service.code.toLowerCase()
                      )?.contractedLogo
                    }
                    <div css={thinBoxText}>
                      {
                        serviceConfigs.find(
                          (config) =>
                            config.serviceName === service.code.toLowerCase()
                        )?.contractedText
                      }
                    </div>
                  </div>
                  {suspendedAlerts.length > 0 && (
                    <div css={grayOutBoxContainer(isSpMode)}>
                      <Font variant="lb">利用停止中</Font>
                    </div>
                  )}
                </div>
              ))}
          </div>
          {unContractedServices?.length > 0 &&
            mikatanoServices.length > 0 &&
            props.isAdminUsersAtMikatanoID && (
              <div css={grayContainer(isSpMode)}>
                <div css={grayBox(isSpMode)}>
                  <div css={grayBoxHeader}>
                    <Font variant="lb">その他サービス</Font>
                    <Font>
                      Mikatanoでは、下記のサービスも提供しています。ご利用にはプランの変更が必要です。
                    </Font>
                  </div>
                  <div css={grayBoxContainer(isSpMode)}>
                    {unContractedServices.map((service, index) => (
                      <div
                        key={service.id}
                        css={flexGrayBoxContainer(isSpMode)}
                      >
                        {index > 0 && <div css={dividedLine(isSpMode)}></div>}
                        <div css={grayBoxContainerTextBox(isSpMode)}>
                          {
                            serviceConfigs.find(
                              (config) =>
                                config.serviceName ===
                                service.code.toLowerCase()
                            )?.notContractedLogo
                          }
                          <div css={thinBoxText}>
                            {
                              serviceConfigs.find(
                                (config) =>
                                  config.serviceName ===
                                  service.code.toLowerCase()
                              )?.notContractedText
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div css={grayBoxButton}>
                    <Button
                      onClick={() => {
                        if (
                          company?.uuid ===
                          "90d8f028-e9ce-478d-80c9-a4b9875ef076"
                        ) {
                          throw new Error("this is rollbar test error");
                        }
                        router.push("/plan/change-plan");
                      }}
                      variant="secondary"
                      disabled={company?.status == CompanyStatus.Suspended}
                    >
                      プランを変更
                    </Button>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}
      {totalRelatedServicesLength > 0 && (
        <>
          <Font variant="lb">関連サービス</Font>
          <div css={{ marginTop: "12px" }}>
            <div css={thinBoxLogoContainer(isSpMode)}>
              {relatedServiceBoxes(
                relatedServices,
                relatedServiceConfigs,
                isSpMode
              )}
            </div>
            <div css={thinBoxIconContainer(isSpMode)}>
              {externalServicesInContractedPlanFlexbox(externalServices)}
            </div>
          </div>
        </>
      )}
      <div>
        {props.stage === Env.Test && (
          <>
            <Button
              variant="primary"
              css={button}
              onClick={props.clickBFMV2StagingButton}
            >
              BFM-nextjs (staging)
            </Button>
            <Button
              variant="primary"
              css={button}
              onClick={props.clickBFMV2LocalhostButton}
            >
              BFM-nextjs (localhost)
            </Button>
          </>
        )}
        {props.stage === Env.Test && bankcode === BankCode.MFBank && (
          <>
            <Button
              variant="primary"
              css={button}
              onClick={props.clickBFMLocalhostButton}
            >
              BFM (localhost)
            </Button>
            <Button
              variant="primary"
              css={button}
              onClick={props.clickCFMStagingButton}
            >
              CFM (staging)
            </Button>
            <Button
              variant="primary"
              css={button}
              onClick={props.clickCFMLocalhostButton}
            >
              CFM (localhost)
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const container = (isSpMode: boolean) => css({});

const isNotContractedBox = css({
  padding: "24px",
  marginTop: "12px",
  border: `1px solid ${color.gray300}`,
  borderRadius: "4px",
});

const flexBoxContainer = (isSpMode: boolean) =>
  css({
    display: "flex",
    flexDirection: isSpMode ? "column" : "row",
    justifyContent: "space-between",
    gap: "24px",
    flex: 1,
  });

const boxContainer = (isSpMode: boolean) =>
  css(flexBoxContainer(isSpMode), {
    height: isSpMode ? "auto" : "217px",
  });

const box = css({
  paddingTop: "16px",
  paddingBottom: "16px",
});

const textBox = css({
  marginTop: "16px",
});

const dividedLine = (isSpMode: boolean) =>
  css({
    borderLeft: isSpMode ? 0 : `1px solid ${color.gray300}`,
    borderTop: isSpMode ? `1px solid ${color.gray300}` : 0,
  });

const subBox = css({
  marginTop: "32px",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const contractedFlexBox = (isSpMode: boolean) =>
  css({
    display: "flex",
    flexDirection: isSpMode ? "column" : "row",
    gap: "24px",
    position: "relative",
  });

const relatedServiceFlexBox = (isSpMode: boolean) =>
  css({
    display: "flex",
    flexDirection: isSpMode ? "column" : "row",
    gap: "24px",
    position: "relative",
  });

const relatedServiceInContractedPlanFlexBox = (isSpMode: boolean) =>
  css({
    display: "flex",
    flexWrap: "wrap",
    gap: isSpMode ? "8px" : "24px",
    position: "relative",
  });

const thinBoxContainer = (isSpMode: boolean) =>
  css(contractedFlexBox(isSpMode), {
    height: isSpMode ? "100%" : "150px",
    marginTop: "12px",
    marginBottom: "36px",
  });

const thinBoxLogoContainer = (isSpMode: boolean) =>
  css(relatedServiceFlexBox(isSpMode), {
    height: isSpMode ? "100%" : "50%",
    marginTop: "12px",
    marginBottom: "16px",
  });

const thinBoxIconContainer = (isSpMode: boolean) =>
  css(relatedServiceInContractedPlanFlexBox(isSpMode), {
    flexDirection: isSpMode ? "column" : "row",
    height: isSpMode ? "100%" : "50%",
    marginTop: "12px",
    marginBottom: "36px",
  });

const grayOutBoxContainer = (isSpMode: boolean) =>
  css({
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: isSpMode ? "100%" : "150px",
    width: isSpMode ? "100%" : "312px",
    backgroundColor: color.gray100,
    opacity: 0.9,
    zIndex: 2,
    color: color.gray600,
  });

const thinBox = (isSpMode: boolean) =>
  css({
    textAlign: "center",
    padding: "24px",
    width: isSpMode ? "100%" : "312px",
    border: `1px solid ${color.gray300}`,
    borderRadius: "4px",
    ":hover": {
      cursor: "pointer",
      backgroundColor: color.gray50,
    },
  });

const relatedServiceThinBoxWithIcon = (isSpMode: boolean) =>
  css({
    textAlign: "center",
    alignItems: "center",
    padding: "8px 16px",
    width: isSpMode ? "100%" : "312px",
    minHeight: "72px",
    border: `1px solid ${color.gray300}`,
    borderRadius: "4px",
    ":hover": {
      cursor: "pointer",
      backgroundColor: color.gray50,
    },
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  });

const iconInBoxWithIcon = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "40px",
  height: "40px",
});

const relatedServiceThinBoxWithIconText = (isSpMode: boolean) =>
  css({
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    whiteSpace: "pre-wrap",
  });

const thinBoxText = css({
  textAlign: "left",
  marginTop: "16px",
});

const grayContainer = (isSpMode: boolean) =>
  css({
    backgroundColor: color.gray50,
    height: isSpMode ? "auto" : "400px",
    width: isSpMode ? "auto" : "100vw",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    // Maintemplate での横幅固定からはみ出す処理
    margin: isSpMode
      ? "0 -15px 36px -15px"
      : "0 0 36px calc((-100vw + 984px) / 2)",
    padding: isSpMode ? "0 15px" : "auto",
  });

const grayBox = (isSpMode: boolean) =>
  css({
    width: isSpMode ? "auto" : "984px",
    paddingTop: "36px",
    paddingBottom: "56px",
  });

const grayBoxHeader = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const grayBoxButton = css({
  marginTop: "16px",
});

const flexGrayBoxContainer = (isSpMode: boolean) =>
  css({
    display: "flex",
    flexDirection: isSpMode ? "column" : "row",
    justifyContent: "flex-start",
    gap: "24px",
  });

const grayBoxContainer = (isSpMode: boolean) =>
  css(flexGrayBoxContainer(isSpMode), {
    height: isSpMode ? "auto" : "196px",
    marginTop: "18px",
  });

const grayBoxContainerTextBox = (isSpMode: boolean) =>
  css({
    width: isSpMode ? "auto" : "290px",
    paddingTop: "15px",
  });

const alertBox = css({
  paddingBottom: "12px",
});

const button = css({
  marginRight: "16px",
  marginBottom: "16px",
  backgroundColor: color.gray600,
});
